import { FlashTimerExtras, PqTimerExtras, TimerExtras } from "../../lib/timer";
import { FlashItemDataSource, ItemDataSource, PqItemDataSource } from "./item";
import { ListDataSource } from "./list";
import { MetadataDataSource } from "./metadata";
import { ScoreDataSource } from "./score";
import { SettingsDataSource } from "./settings";
import { AttemptState, GlobalState, StateDataSource } from "./state";
import { StateInitializer } from "./stateinitializer";

/**
 * ProductId is a unique identifier for a practice question product - e.g. TEAS6PQ exams.
 */
export interface ProductId { }
/**
 * AttemptId uniquely identifies each attempt of a quiz within a product.
 */
export interface AttemptId { }
/**
 * QuizId is an object key type (string | number | symbol) identifying a quiz within a PQ product.
 * Used for OverviewScores and ReviewScores.
 */
export interface QuizId { }
export type QuizIndex = string;
/**
 * ItemId serves as a unique identifier for each item within an attempt.
 */
export interface ItemId { }

export interface Serializer<T> {
	parse(text: string): T;
	toString(val: T): string;
}

export class Serializer<T> implements Serializer<T> {
	toString(val: T): string {
		return (typeof val == 'object' ? JSON.stringify(val) : val).toString();
	}
	parse(text: string | undefined): T | undefined {
		if (text === undefined) { return undefined; }
		if (/^\d+\.\d+$/.test(text)) { return text as any as T; }
		try {
			return JSON.parse(text) as T ?? undefined;
		} catch (e) {
			return text as any as T;
		}
	}
}

export abstract class SerializerFactory<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> {
	abstract productSerializer(): Serializer<P>;
	abstract attemptSerializer(): Serializer<A>;
	abstract quizSerializer(): Serializer<Z>;
	abstract itemSerializer(): Serializer<I>;
}

export class DefaultSerializerFactory<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends SerializerFactory<P, A, Z, I> {
	constructor() { super(); }
	productSerializer(): Serializer<P> { return new Serializer(); }
	attemptSerializer(): Serializer<A> { return new Serializer(); }
	quizSerializer(): Serializer<Z> { return new Serializer(); }
	itemSerializer(): Serializer<I> { return new Serializer(); }
}

export abstract class DataSourceFactory<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId, E extends TimerExtras> {
	abstract listDataSource(item: Serializer<I>): ListDataSource<I>;
	abstract metadataDataSource(): MetadataDataSource<P, A>;
	abstract itemDataSource(): ItemDataSource<P, A, I, E>;
	abstract scoreDataSource(quiz: Serializer<Z>): ScoreDataSource<A, Z>;
	abstract settingsDataSource(): SettingsDataSource<P>;
	abstract globalStateDataSource(initializer: StateInitializer<GlobalState>): StateDataSource<GlobalState>;
	globalStateInitializer(): StateInitializer<GlobalState> | undefined { return undefined; };
	abstract attemptStateDataSource(initializer: StateInitializer<AttemptState>): StateDataSource<AttemptState>;
	attemptStateInitializer(): StateInitializer<AttemptState> | undefined { return undefined; };
}

export abstract class PqDataSourceFactory<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends DataSourceFactory<P, A, Z, I, PqTimerExtras> {
	abstract itemDataSource(): PqItemDataSource<P, A, I>;
}

export abstract class FlashDataSourceFactory<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends DataSourceFactory<P, A, Z, I, FlashTimerExtras> {
	abstract itemDataSource(): FlashItemDataSource<P, A, I>;
}

export {
	ItemDataSource, PqItemDataSource, FlashItemDataSource, ListDataSource,
	MetadataDataSource, ScoreDataSource, SettingsDataSource, StateDataSource,
	StateInitializer
};
